import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withScope } from '@bingads-webui-react/scope-service';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { InstrumentationContext, withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { log } from '@bingads-webui-campaign-react/step-wizard-machine';
import { stepListProp } from '@bingads-webui-campaign-react/step-wizard-machine-capi';
import { VerticalSeparatedLayout } from '@bingads-webui-react/steps';
import { getWizardStyles } from '../styles/wizard-styles';
import { useWizardState } from './use-wizard-state';
import { MultiStepLayout } from './multi-step-layout';
import { SingleStepLayout } from './single-step-layout';

const { Step } = VerticalSeparatedLayout;

const StepWizardImpl = ({
  wizardScenario, instrumentation, extraStepsData, permissions, scope, routeToNavigateToWhenDone, scopeToRemoveWhenDone, perfMarker,
}) => {
  useEffect(() => {
    log('StepWizard mounted');
    return () => {
      log('StepWizard unmounted');
    };
  }, []);

  const classes = useDefaultStyles(getWizardStyles);

  const [wizardState, wizardActions] = useWizardState({
    permissions,
    wizardScenario,
    scope,
    routeToNavigateToWhenDone,
    scopeToRemoveWhenDone,
    instrumentation,
    extraStepsData,
    perfMarker,
  });

  const {
    isCenteredLayout,
    withContentFooter = true,
    i18n,
    scenarioName,
    scenario,
    stepListWithServiceInfo,
    isBusy,
    wasSaving,
    wasWaitingForConfirmation,
    wasStopping,
  } = wizardState;

  const Layout = useMemo(
    () => (wizardScenario.stepList.length > 1 ? MultiStepLayout : SingleStepLayout),
    [wizardScenario.stepList.length]
  );

  const loadingText = useMemo(
    () => {
      if (wasWaitingForConfirmation) {
        return i18n.getString(_TL_('Waiting for confirmation...'));
      }
      if (wasStopping) {
        return i18n.getString(_TL_('Finishing...'));
      }
      return wasSaving ? i18n.getString(_TL_('Saving...')) : i18n.getString(_TL_('Loading...'));
    },
    [wasWaitingForConfirmation, wasSaving, wasStopping, i18n]
  );

  const steps = useMemo(() => stepListWithServiceInfo
    .map(({ stepInfo: stepInfo2, stepService, StepRepository }) => (
      <Step
        className={classes.wizardStep}
        title={i18n.getString(stepInfo2.title)}
        path={stepInfo2.id}
        key={stepInfo2.id}
        enabled
        enableRenderingLog
      >
        <Activity name={stepInfo2.id}>
          {stepService && stepService.initialized &&
          <>
            {StepRepository &&
              <StepRepository stepService={stepService} />
            }
            <stepInfo2.StepComponent
              stepService={stepService}
              wizardActions={wizardActions}
              wizardPerfMarker={perfMarker}
              stepPerfMarkerName={stepInfo2.id}
            />
          </>
          }
          {/* the below element is there because StepContainer complains if Step.children is null,
          even though it is not going to render them. It is a bug in Step component. It scans the React VDOM tree to
          extract meta-information about steps, nested steps and so on.  */}
          {!stepService &&
            <p>Loading...</p>
          }
        </Activity>
      </Step>
    )), [stepListWithServiceInfo, classes.wizardStep, i18n, wizardActions, perfMarker]);

  return (
    <InstrumentationContext.Provider value={instrumentation}>
      <Scenario name={scenario.name}>
        <Activity name="Wizard">
          <div className={classNames(scenarioName.toLowerCase(), classes.uiBlockerAdapter)}>
            <UiBlocker
              blocking={isBusy}
              loadingText={loadingText}
            >
              <Layout wizardState={wizardState} wizardActions={wizardActions} classes={classes} isCenteredLayout={isCenteredLayout}>
                {steps}
              </Layout>
              {withContentFooter && <ContentFooter />}
            </UiBlocker>
          </div>
        </Activity>
      </Scenario>
    </InstrumentationContext.Provider>
  );
};

StepWizardImpl.propTypes = {
  permissions: PropTypes.shape({}).isRequired,
  wizardScenario: PropTypes.shape({
    scenarioName: PropTypes.string.isRequired,
    stepList: stepListProp.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  routeToNavigateToWhenDone: PropTypes.string.isRequired,
  scopeToRemoveWhenDone: PropTypes.arrayOf(PropTypes.string),
  instrumentation: PropTypes.shape({}).isRequired,
  extraStepsData: PropTypes.shape({}),
  perfMarker: PropTypes.shape({}).isRequired,
};

StepWizardImpl.defaultProps = {
  extraStepsData: {},
  scopeToRemoveWhenDone: [],
};

export const StepWizard = withInstrumentation(withScope(StepWizardImpl));
